import { LockOutlined } from '@ant-design/icons';
import { type TipoComunicazioneDTO } from '@framework/api-client/api/inquiries-compile/schemas/tipoComunicazioneDTO.ts';
import { useField } from '@framework/form/field/useField.tsx';
import { DateInput } from '@framework/form/inputs/DateInput.tsx';
import { StaticFormInput } from '@framework/form/inputs/static/StaticFormInput.tsx';
import { TextAreaInput } from '@framework/form/inputs/TextAreaInput.tsx';
import { TextInput } from '@framework/form/inputs/TextInput.tsx';
import { FormItem } from '@framework/form/item/FormItem.tsx';
import { RowElement } from '@framework/ui/components/row/RowElement.tsx';
import { VerticalSpace } from '@framework/ui/components/space/VerticalSpace.tsx';
import { Tooltip, Typography } from 'antd';
import dayjs from 'dayjs';
import { useMemo } from 'react';
import { useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { type CreateCommunicationRequest } from '../../../schema/communication/CreateCommunicationRequestSchema.ts';
import { renderPlaceholderText } from '../detail/renderPlaceholderText.tsx';
import { CommunicationReceiverInput } from './components/CommunicationReceiverInput.tsx';
import { CommunicationSenderInput } from './components/CommunicationSenderInput.tsx';
import { InputRenderRow } from './styled/InputRenderRow.tsx';

const { Text } = Typography;

interface CommunicationTypeFieldProps {
  communication: CreateCommunicationRequest | null | undefined;
  communicationType: TipoComunicazioneDTO;
  isManual?: boolean;
}

/**
 * Campi una comunicazione in base al Tipo di Comunicazione
 */
export function CommunicationTypeFields(props: CommunicationTypeFieldProps) {
  const { communicationType, isManual, communication } = props;
  const { t } = useTranslation();

  const todayDate = useMemo(() => dayjs(), []);

  const communicationMessage = useWatch({ name: 'messaggio' });

  // sostituire con flag configurazione
  const isSubjectEditable = !!communicationType.flOggettoEditabile || isManual;
  const isMessageEditable =
    !!communicationType.flMessaggioEditabile || isManual;
  const hasAdditionalNotes = !!communicationType.flNoteAddizionali || isManual;

  const { setValue: subjectEditFlag } = useField('flOggettoModificato');
  const { setValue: messageEditFlag } = useField('flMessaggioModificato');

  return (
    <div>
      {/* Il campo mittente viene visualizzato solo se la comunicazione è manuale */}
      {isManual && (
        <CommunicationSenderInput communicationType={communicationType} />
      )}

      {/* Campo destinatarila comunicazione */}
      <InputRenderRow
        title={t('communication-type.receivers', 'Destinatari')}
        input={
          <CommunicationReceiverInput
            communicationType={communicationType}
            communication={communication}
            isManual={isManual}
          />
        }
      />

      {/* dati protocollo comunicazioni caricate manualmente */}
      {isManual && (
        <>
          <InputRenderRow
            title={t('communications.protocol-row-label', 'Protocollo')}
            input={
              <RowElement>
                <FormItem
                  layout={{ removeHelpMargin: true }}
                  name="dataProtocollo"
                  label={t('communications.protocol-date', 'Data Protocollo')}
                >
                  <DateInput
                    showTime
                    type="datetime"
                    format="DD/MM/YYYY HH:mm:ss"
                    maxDate={todayDate}
                    name="dataProtocollo"
                  />
                </FormItem>
                <FormItem
                  layout={{ removeHelpMargin: true }}
                  name="numeroProtocollo"
                  label={t(
                    'communications.protocol-number',
                    'Numero Protocollo',
                  )}
                >
                  <TextInput name="numeroProtocollo" />
                </FormItem>
              </RowElement>
            }
          />
        </>
      )}

      {/* Campo oggetto della comunicazione */}
      <InputRenderRow
        title={t('communications.subject', 'Oggetto')}
        input={
          <FormItem
            layout={{ removeHelpMargin: true }}
            name="oggetto"
            label={t('communications.subject', 'Oggetto')}
          >
            {isSubjectEditable ? (
              <TextInput
                name="oggetto"
                placeholder={t(
                  'communications.subject-placeholder',
                  'Oggetto della comunicazione',
                )}
                onChange={() => {
                  subjectEditFlag(true);
                }}
              />
            ) : (
              <StaticFormInput
                name="oggetto"
                size="large"
                suffix={
                  <Tooltip title="Oggetto non modificabile">
                    <LockOutlined aria-hidden="true" />
                  </Tooltip>
                }
              />
            )}
          </FormItem>
        }
      />

      {/* Campo Messaggio della comunicazione */}
      {isMessageEditable ? (
        <FormItem
          layout={{ removeHelpMargin: true }}
          name="messaggio"
          label={t('communications.message', 'Messaggio')}
        >
          <TextAreaInput
            name="messaggio"
            placeholder={t(
              'communications.message-placeholder',
              'Messaggio della comunicazione',
            )}
            rows={4}
            onChange={() => {
              messageEditFlag(true);
            }}
          />
        </FormItem>
      ) : (
        <>
          <VerticalSpace />
          <div>
            <Text type="secondary" style={{ fontSize: '14px' }}>
              Testo predefinito della comunicazione:
            </Text>
          </div>
          <div>
            <Text style={{ fontSize: '16px' }}>
              {renderPlaceholderText(communicationMessage)}
            </Text>
          </div>
          <VerticalSpace />
        </>
      )}

      {hasAdditionalNotes && !isManual && (
        <FormItem
          name="noteAddizionali"
          label={t('communications.additional-notes', 'Note addizionali')}
        >
          <TextAreaInput
            name="noteAddizionali"
            placeholder={t(
              'communications.additional-notes-placeholder',
              'Note addizionali alla comunicazione',
            )}
            rows={4}
          />
        </FormItem>
      )}

      <VerticalSpace />
    </div>
  );
}
